import { Component, Input, OnInit } from '@angular/core';
import { EntryConfigInterface, FormConfig, PageConfigInterface } from '@ptl/forms';
import { ContentStorageService } from '@ptl/global-components';
import { ReducerUtilityService } from '@ptl/state';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { FormConfigUtilityService } from 'src/app/angular-elements/form-wrappers/services/filterByConfigMetatags/form-config-utility.service';
import { DateService } from 'src/app/shared/services/date.service';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
//import * as moment from 'moment';
import moment from 'moment';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpHeaders } from '@angular/common/http';

interface NotificationAndAuthorizationSignaturesMap {
  "subscriber": {
    "name": {
      "first": {
        "controlLabel": string;
        "replacementText": "{replaceMe.subscriberFirstName}";
      };
      "last": {
        "controlLabel": string;
        "replacementText": "{replaceMe.subscriberLastName}";
      }
    }
  };
  "spouse": {
    "header": string;
    "name": {
      "first": {
        "controlLabel": string;
        "replacementText": "{replaceMe.spouseFirstName}";
      };
      "last": {
        "controlLabel": string;
        "replacementText": "{replaceMe.spouseLastName}";
      }
    }
  };
  "producer": {
    "name": {
      "fullName": {
        "controlLabel": string;
        "replacementText": "{replaceMe.producerFullName}";
      }
    }
  }
  "date": {
    "replacementText": "{replaceMe.date}";
  };
}

/**
 * This component behaves similar to the individual-page-review.component
 * 1) It fetches a defined FormsMap
 * 2) It fetches all of those pages
 * 3) It parses all the entries in those pages
 *  3a) It then beautifies [ read: dates look good, as do numbers ],
 *  3b) filters [ read: gets rid of questions that are unanswered; text that isn't part of an answered question ]
 *  3c) and appends all the label+answer variants
 * 
 * The one thing it does quite differently, is that, at present, it is forced to only take into account the final page of the passed in form
 * because this component was developed to simple resolve an outstanding issue related to the INDIVIDUAL ENROLLMENT POST-SUBMISSION PRINT content
 * 1) legal content needed comes on the last page
 * 2) the population of the content to be stored in the .print-application-form class'd div comes from the individual-page-review.component
 * 3) the population of the content does not take into account any items AFTER the "current" [read: individual-page-review.component on the form.pages.length -1 ]
 * 4) nor does the population of the content take into account anything that has no value ( which the final page would not have during the review )
 * 
 * This component then appends the final page content, with a hard-coded "YES" value ( because you can't get to this page without having attested your agreement, and because the value doesn't exist at this point )
 * to the content added to the same location ( .print-application-form class'd div ) after a little timeout ( it worked without, but only due to async, and I didn't think that was safe )
 */
@Component({
    selector: 'app-confirmation-page-addendum',
    templateUrl: './confirmation-page-addendum.component.html',
    styleUrls: ['./confirmation-page-addendum.component.scss'],
    standalone: false
})
export class ConfirmationPageAddendumComponent implements OnInit {

  @Input() metaTagPriorityByIndex: string;
  @Input() formsMapJsonUrl: string;

  strategyName: string = 'app-confirmation-page-addendum';

  onlyAttachFinalPage = true;

  constructor(
    private httpClient: HTTPWrapperContext,
    private formConfigUtility: FormConfigUtilityService,
    private contentStorage: ContentStorageService,
    private reducerUtilityService: ReducerUtilityService,
    private commonUtilityService: CommonUtilityService,
    private dateUtility: DateService,
    private domSanatizer: DomSanitizer
  ) { }

  async ngOnInit() {
    if (this.metaTagPriorityByIndex && this.formsMapJsonUrl) {
      try {
        const formConfig: { formConfiguration: { pages: string[] } } = await this.getForm();
        const content = await this.parseForm(formConfig);

        const domParser = new DOMParser();
        const parsedContent = domParser.parseFromString(content, 'text/html');
        const innerTextContent = parsedContent.getElementsByTagName('body')[0].innerText;

        // This is disconnected from the flow established in the confirmation-page.component, so let's just wait an instant [ though this is async, you never know! ]
        setTimeout(() => {
          const element: HTMLElement = document.createElement("div")
          element.classList.add('begin-app-content');

          const printAppElement = document.getElementsByClassName('print-application-form')[0];
          let textFieldInPrintApp = printAppElement.children[printAppElement.children.length - 1];
          textFieldInPrintApp.innerHTML += innerTextContent;
        }, 300);
      } catch (e) {
        console.warn(e);
      }
    }
  };


  async getForm() {
    const strategyMap: Map<string, any> = new Map();
    strategyMap.set("hardcoded", false);
    strategyMap.set("metaTagPriorityByIndex", this.metaTagPriorityByIndex || '');
    strategyMap.set("url", this.formsMapJsonUrl);
    this.contentStorage.storeContentMapFromComponent(this.strategyName, strategyMap);
    const formConfig = await this.formConfigUtility.getJsonFormConfig(this.strategyName) as any;
    const form = await this.getJsonMap(formConfig.url) as any;
    return form;
  }

  /** 
   * valueExists: boolean for the sake of saying "hey, this thing should be converted to boolean, regardless of value"
   */
  async parseForm(formConfig: { formConfiguration: { pages: string[] } }) {
    let content = '';
    const allPages = await Promise.all(formConfig.formConfiguration.pages.map(p => this.getJsonMap(p)));

    allPages.forEach((page: PageConfigInterface, pageNumber) => {

      if (!this.onlyAttachFinalPage || pageNumber === allPages.length - 1) {
        // This is good
        /**
         * ============================
         * = This Format is Generated =
         * ============================
         */
        // const pageHeaderSeparator = '======================================';
        // content += `\n\n${pageHeaderSeparator}\n`;
        // let pageHeader = "= " + page.pageHeader + (() => {
        //   let retVal = '';
        //   let appendLength = pageHeaderSeparator.length - page.pageHeader.length - 3;
        //   if (appendLength > 0) {
        //     while (retVal.length < appendLength) {
        //       retVal += " ";
        //     }
        //     retVal += "=";
        //     return retVal;
        //   }
        // })();
        // content += `${pageHeader}\n`;
        // content += `${pageHeaderSeparator}\n\n`;

        // This is gnarly, but aligns with our stylistic choices
        /** Match the initial styling of section separation */
        content += `____\n\n${page.pageHeader}\n---------------------------------------------------------\n`;

        let entriesAdded = 0;

        page.entries.forEach((entry, entryNumber) => {

          const isControlField = (
            entry.formControlAttributes &&
            entry.formControlAttributes.connectPath &&
            entry.formControlAttributes.formControlName
          );

          const isContentFieldForReview = (
            entry.custom?.displayOnPrintReview
          );

          let controlValue = null;

          if (isControlField) {

            const payload = this.reducerUtilityService.getPayloadStates();
            const dataParsed = this.commonUtilityService.setNestedProperty(payload, entry.formControlAttributes.connectPath, 'ConfirmationPageAddendum');
            const value = dataParsed[entry.formControlAttributes.formControlName];

            if (value !== null && value !== undefined) {

              switch (entry.base.componentType) {

                case 'text-input':
                  controlValue = value;
                  break;

                case 'date-input':
                case 'coverage-dropdown-select':
                  controlValue = this.dateUtility.getDateAsFormattedString(value.toString(), 'MM/DD/YYYY');
                  break;

                case 'radio-input':
                case 'custom-radio-input':
                  let radioValue = null;
                  for (const option in entry.radioGroupAttributes.optionAttributes) {
                    if (entry.radioGroupAttributes.optionAttributes[option].value === value) {
                      radioValue = entry.radioGroupAttributes.optionAttributes[option].label;
                    }
                  }
                  controlValue = radioValue;
                  break;

                case 'dropdown-select':
                  let dropdownValue = null;
                  for (const option in entry.selectAttributes.optionAttributes) {
                    if (entry.selectAttributes.optionAttributes[option].value === value) {
                      dropdownValue = entry.selectAttributes.optionAttributes[option].label;
                    }
                  }
                  controlValue = dropdownValue;
                  break;

                case 'checkbox-input':
                case 'custom-checkbox-input':
                  controlValue = this.convertTruthyFalsyToString(value);
                  break;

                case 'custom-date-input':
                  controlValue = moment(value).format('MM/DD/YYYY');
                  break;

                default:
                  controlValue = value;
                  break;

              }

            }

          } else if (isContentFieldForReview) {
            controlValue = this.retrieveSafeHTML(entry)
            if (controlValue) {
              controlValue = controlValue.toString().replace(/(<br\/>|<br>|<br \/>)/g, '\n');
            }
          }

          const controlHasValue = controlValue !== null && controlValue !== undefined && controlValue !== '';
          
          if (controlHasValue || (entry?.custom?.displayOnPrintReview && entry.custom?.displayOnPrintReviewLabel)) {
            // Spacer
            if (entriesAdded > 0) {
              content += `----------\n`;
            }
            entriesAdded++;

            /** BAU flow - value and label exist - display them */
            if (controlHasValue) {

              // Value
              if (entry.formControlAttributes?.label) {
                content += `${entry.formControlAttributes?.label}\n`;
              }

              content += `${controlValue}\n`;
              
            }
            /** Post-Review page flow - label exists but value is not present in plaintext review ( we provide mock data here, occasionally providing replacement values: {replaceMe} ) */
            else if (entry?.custom?.displayOnPrintReview && entry.custom?.displayOnPrintReviewLabel) {
              content += `${entry.custom?.displayOnPrintReviewLabel}\n`;
            }

          }
        });

        content = this.replaceNotificationAndAuthorizationValues(content, page?.custom?.replacementMap);

      }

    });

    return content;
  }

  /**
   * Because we also need to have legal content from the Notification & Authorization pages
   *  
   * 1) This is configured such that the regex searches can be configured ( in case "First Name" changes to "Primary Name" or something)
   * 2) Replacement map and values for controls will be aligned in the JSON file
   * 
   * This functionality takes the plaintext sessionStorage data, and essentially cuts it down as we find matches for
   * the text for each of the relevant parties:
   * 
   * FirstName: x, LastName: y, SpouseFirst: z, SpouseLast: a, ProducerName: b
   *               LastName: y, SpouseFirst: z, SpouseLast: a, ProducerName: b
   *                            SpouseFirst: z, SpouseLast: a, ProducerName: b
   *                            SpouseFirst: z, SpouseLast: a, ProducerName: b
   *                                            SpouseLast: a, ProducerName: b
   *                                                           ProducerName: b
   **/
  replaceNotificationAndAuthorizationValues(content: string, replacementMap: NotificationAndAuthorizationSignaturesMap) {
    let subscriberFirstNameValue;
    let subscriberLastNameValue;
    let spouseFirstNameValue;
    let spouseLastNameValue;
    let producerNameValue; // Only ever saved as the full name

    let stringifiedAppForm = this.commonUtilityService.base64decode(sessionStorage.getItem('applicationForm'));

    // Subscriber First name
    let subscriberFirstNameLabel = `${replacementMap.subscriber.name.first.controlLabel}\n`; // 'First Name\n'
    let subscriberFirstNameIndex = stringifiedAppForm.search(subscriberFirstNameLabel);
    stringifiedAppForm = stringifiedAppForm.slice(subscriberFirstNameIndex + subscriberFirstNameLabel.length - stringifiedAppForm.length);
    let newLineAfterSubscriberFirstNameValue = stringifiedAppForm.search('\n');
    subscriberFirstNameValue = stringifiedAppForm.slice(0, newLineAfterSubscriberFirstNameValue)
    // console.log(subscriberFirstNameValue);

    // Subscriber Last name
    let subscriberLastNameLabel = `${replacementMap.subscriber.name.last.controlLabel}\n`; // 'Last Name\n'
    let subscriberLastNameIndex = stringifiedAppForm.search(subscriberLastNameLabel);
    stringifiedAppForm = stringifiedAppForm.slice(subscriberLastNameIndex + subscriberLastNameLabel.length - stringifiedAppForm.length);
    let newLineAfterSubscriberLastNameValue = stringifiedAppForm.search('\n');
    subscriberLastNameValue = stringifiedAppForm.slice(0, newLineAfterSubscriberLastNameValue)
    // console.log(subscriberLastNameValue);

    let spouseHeader = `${replacementMap.spouse.header}\n`; // 'Spousal Information\n';
    if (stringifiedAppForm.search(spouseHeader) !== -1) {
      // Spouse First name
      let spouseFirstNameLabel = `${replacementMap.spouse.name.first.controlLabel}\n`; // 'First Name\n';
      let spouseFirstNameIndex = stringifiedAppForm.search(spouseFirstNameLabel);
      stringifiedAppForm = stringifiedAppForm.slice(spouseFirstNameIndex + spouseFirstNameLabel.length - stringifiedAppForm.length);
      let newLineAfterSpouseFirstNameValue = stringifiedAppForm.search('\n');
      spouseFirstNameValue = stringifiedAppForm.slice(0, newLineAfterSpouseFirstNameValue)
      // console.log(spouseFirstNameValue);

      // Spouse Last name
      let spouseLastNameLabel = `${replacementMap.spouse.name.last.controlLabel}\n`; // 'Last Name\n';
      let spouseLastNameIndex = stringifiedAppForm.search(spouseLastNameLabel);
      stringifiedAppForm = stringifiedAppForm.slice(spouseLastNameIndex + spouseLastNameLabel.length - stringifiedAppForm.length);
      let newLineAfterSpouseLastNameValue = stringifiedAppForm.search('\n');
      spouseLastNameValue = stringifiedAppForm.slice(0, newLineAfterSpouseLastNameValue)
      // console.log(spouseLastNameValue);
    }

    // Producer Full Name
    let producerNameLabel = `${replacementMap.producer.name.fullName.controlLabel}\n`; // 'Producer Name\n';
    let producerNameIndex = stringifiedAppForm.search(producerNameLabel);
    if (producerNameIndex !== -1) {
      stringifiedAppForm = stringifiedAppForm.slice(producerNameIndex + producerNameLabel.length - stringifiedAppForm.length);
      let newLineAfterProducerNameValue = stringifiedAppForm.search('\n');
      producerNameValue = stringifiedAppForm.slice(0, newLineAfterProducerNameValue)
      // console.log(producerNameValue);
    }

    if (replacementMap?.producer?.name?.fullName?.replacementText) {
      content = content.replace(replacementMap.producer.name.fullName.replacementText, producerNameValue);
    }
    if (replacementMap?.subscriber?.name?.first?.replacementText) {
      content = content.replace(replacementMap.subscriber.name.first.replacementText, subscriberFirstNameValue);
    }
    if (replacementMap?.subscriber?.name?.last?.replacementText) {
      content = content.replace(replacementMap.subscriber.name.last.replacementText, subscriberLastNameValue);
    }
    if (replacementMap?.spouse?.name?.first?.replacementText) {
      content = content.replace(replacementMap.spouse.name.first.replacementText, spouseFirstNameValue);
    }
    if (replacementMap?.spouse?.name?.last?.replacementText) {
      content = content.replace(replacementMap.spouse.name.last.replacementText, spouseLastNameValue);
    }
    if (replacementMap?.date?.replacementText) {
      content = content.replace(replacementMap.date.replacementText, this.dateUtility.getTodayAsMoment().format('MM/DD/YYYY'));
    }

    return content;

  }

  /** For use with non-control entries ( like specified legal text that is present on review/print ) */
  retrieveSafeHTML(entry: EntryConfigInterface) {

    if (entry?.custom?.textSafeHtml) {
      return this.domSanatizer.bypassSecurityTrustHtml(entry.base.text);
    }

    return entry?.base?.text;
  }

  convertTruthyFalsyToString(value, returnTruthyString?, returnFalseyString?) {
    let returnValue = value ? value : 'Not Answered';

    if (['Yes', 'yes', 'YES', true, 'true', 'Y', 'y'].includes(value)) {
      returnValue = returnTruthyString || 'Yes';
    }
    if (['No', 'no', 'NO', false, 'false', 'N', 'n'].includes(value)) {
      returnValue = returnFalseyString || 'No';
    }

    return returnValue;
  }

  async getJsonMap(url: string) {

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: url,
      headers: new HttpHeaders({})
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.MOCK);
    return this.httpClient.makeRequest(requestConfig);

  }
}
